import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { store, api, csrfToken, timeAgo } from "../editor/store"
import { fuzzyFilter } from "../editor/fuzzy"
import { repaintMentions } from "../editor/mentions"
import { buildInspectorPane, refreshInspectorHighlights } from "../panes/inspector"
import { FlowPaneController } from "../panes/flow"
import { buildHistoryPane } from "../panes/history"

const ENTITY_KINDS = ["character", "location", "thread", "item", "faction"]
const ALL_KINDS = [...ENTITY_KINDS, "other"]

const KIND_TEXT_CLASSES = {
  character: "text-nord9", location: "text-nord12", thread: "text-nord14",
  item: "text-nord13", faction: "text-nord15", other: "text-nord4"
}

export default class extends Controller {
  static targets = [
    "binder", "editor", "editorColumn", "paneStack", "pane",
    "paletteOverlay", "paletteInput", "paletteList",
    "saveStatus", "version", "wordCount", "data"
  ]
  static values = {
    entitiesUrl: String,
    newDocumentUrl: String,
    projectUrl: String,
    flowUrl: String,
    documentId: Number
  }

  connect() {
    const bootstrap = JSON.parse(this.dataTarget.textContent)
    store.init({ ...bootstrap, urls: { entities: this.entitiesUrlValue } })
    this.documentOptions = bootstrap.documentOptions || {}

    const remembered = this.documentOptions.inspector?.highlighted || []
    remembered.forEach((id) => store.highlighted.add(id))
    if (remembered.length) {
      setTimeout(() => {
        const view = this.editorController()?.view
        if (view) repaintMentions(view)
      }, 50)
    }

    this.keydown = this.handleKeydown.bind(this)
    window.addEventListener("keydown", this.keydown)
    this.paletteMode = null
    this.paletteIndex = 0
    this.flowController = null
    this.lastSavedAt = new Date(bootstrap.savedAt || Date.now())
    this.savedTicker = setInterval(() => this.refreshVersionReadout(), 60_000)

    window.ootline = {
      visit: (url) => Turbo.visit(url),
      toggleInspector: () => this.toggleInspector(),
      toggleFlow: () => this.toggleFlow(),
      toggleHistory: () => this.toggleHistory(),
      createDocument: () => this.createDocument(),
      visitSibling: (offset) => this.visitSibling(offset),
      openEntity: (id) => {
        const entity = store.findEntity(id)
        if (entity) this.openEntity({ detail: { url: entity.url, id: entity.id } })
      },
      text: () => this.editorText(),
      focusEditor: () => this.editorController()?.focus(),
      scrollTo: (pos) => {
        const view = this.editorController()?.view
        if (!view) return
        view.dispatch({ selection: { anchor: pos }, scrollIntoView: true })
        view.focus()
      },
      toggleHighlightEntity: (id) => this.toggleEntityHighlight(id),
      context: () => ({
        projectUrl: this.projectUrlValue,
        documentId: this.documentIdValue,
        documentsUrl: `${this.projectUrlValue}/documents`,
        entitiesUrl: this.entitiesUrlValue
      })
    }
  }

  disconnect() {
    window.removeEventListener("keydown", this.keydown)
    clearInterval(this.savedTicker)
    delete window.ootline
  }

  refreshVersionReadout(version = this.lastVersion) {
    if (version) this.lastVersion = version
    if (!this.lastVersion) return
    this.versionTarget.textContent = `v${this.lastVersion} · saved ${timeAgo(this.lastSavedAt)}`
  }

  editorController() {
    return this.application.getControllerForElementAndIdentifier(this.editorTarget, "editor")
  }

  editorText() {
    return this.editorController()?.view?.state.doc.toString() || ""
  }

  // ---------- global keyboard ----------

  handleKeydown(event) {
    if (event.defaultPrevented) return
    const mod = event.metaKey || event.ctrlKey

    if (mod && !event.altKey && !event.shiftKey && ["k", "p"].includes(event.key.toLowerCase())) {
      event.preventDefault()
      this.paletteVisible ? this.closePalette() : this.openPalette()
    } else if (event.key === "Escape") {
      event.preventDefault()
      if (this.paletteVisible) this.closePalette()
      else if (this.paneTargets.length) this.closeTopPane()
      else if (this.nativeShell) this.postToShell("escape")
      else this.toggleBinder()
    } else if (mod && event.key === "\\") {
      event.preventDefault()
      this.toggleBinder()
    } else if (mod && event.altKey && event.code === "KeyN") {
      event.preventDefault()
      this.createDocument()
    } else if (mod && event.altKey && event.key === "ArrowUp") {
      event.preventDefault()
      this.visitSibling(-1)
    } else if (mod && event.altKey && event.key === "ArrowDown") {
      event.preventDefault()
      this.visitSibling(1)
    } else if (mod && event.altKey && event.key === "[") {
      event.preventDefault()
      this.cycleFocus(-1)
    } else if (mod && event.altKey && event.key === "]") {
      event.preventDefault()
      this.cycleFocus(1)
    } else if (mod && event.shiftKey && ["KeyO", "KeyI"].includes(event.code)) {
      event.preventDefault()
      this.nativeShell ? this.postToShell("toggleInspector") : this.toggleInspector()
    } else if (mod && event.shiftKey && event.code === "KeyF") {
      event.preventDefault()
      this.nativeShell ? this.postToShell("toggleFlow") : this.toggleFlow()
    } else if (mod && event.shiftKey && event.code === "KeyH") {
      event.preventDefault()
      this.nativeShell ? this.postToShell("toggleHistory") : this.toggleHistory()
    } else if (["ArrowLeft", "ArrowRight"].includes(event.key) && !mod && !event.altKey && this.stripNavigable()) {
      event.preventDefault()
      this.cycleFocus(event.key === "ArrowLeft" ? -1 : 1)
    } else if (["ArrowUp", "ArrowDown"].includes(event.key) && this.paneRowNavigable()) {
      event.preventDefault()
      this.movePaneRow(event.key === "ArrowDown" ? 1 : -1)
    }
  }

  stripNavigable() {
    const active = document.activeElement
    if (!active || active === document.body) return false
    if (active.closest(".cm-content, input, textarea, select, [contenteditable]")) return false
    if (this.binderTarget.contains(active)) return true
    return this.paneTargets.some((pane) => pane.contains(active) || pane === active)
  }

  paneRowNavigable() {
    const active = document.activeElement
    if (!active || active.closest(".cm-content, input, textarea, [contenteditable]")) return false
    return this.paneTargets.some((pane) => pane === active || pane.contains(active))
  }

  movePaneRow(direction) {
    const pane = this.paneTargets.find((p) => p === document.activeElement || p.contains(document.activeElement))
    if (!pane) return
    const rows = [...pane.querySelectorAll("button, a")]
    if (!rows.length) return
    const index = rows.indexOf(document.activeElement)
    const next = rows[(index + direction + rows.length) % rows.length]
    next.focus()
  }

  get nativeShell() {
    return !!window.webkit?.messageHandlers?.ootlineNative
  }

  postToShell(action) {
    window.webkit.messageHandlers.ootlineNative.postMessage(action)
  }

  toggleBinder() {
    if (this.nativeShell) {
      this.postToShell("toggleSidebar")
      return
    }
    this.binderTarget.classList.toggle("-ml-64")
  }

  visitSibling(offset) {
    const docs = store.documents
    const index = docs.findIndex((doc) => doc.id === this.documentIdValue)
    const next = docs[index + offset]
    if (next) Turbo.visit(next.url)
  }

  async createDocument() {
    const response = await fetch(this.newDocumentUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
      body: "{}"
    })
    if (response.ok) Turbo.visit(response.url)
  }

  cycleFocus(direction) {
    const regions = [
      this.binderVisible ? this.binderTarget : null,
      this.editorColumnTarget,
      ...this.paneTargets
    ].filter(Boolean)

    const active = document.activeElement
    let index = regions.findIndex((region) => region.contains(active))
    if (index === -1) index = 1
    const next = regions[(index + direction + regions.length) % regions.length]
    this.focusRegion(next)
  }

  get binderVisible() {
    return !this.binderTarget.classList.contains("-ml-64")
  }

  focusRegion(region) {
    if (region === this.editorColumnTarget) {
      this.editorController()?.focus()
    } else if (region === this.binderTarget) {
      (region.querySelector('[aria-current="page"]') || region.querySelector(".binder-item"))?.focus()
    } else {
      region.focus()
    }
  }

  // ---------- panes ----------

  async openEntity({ detail: { url, id } }) {
    const existing = this.paneTargets.find((pane) => pane.dataset.entityId === String(id))
    if (existing) {
      existing.focus()
      return
    }
    const response = await fetch(url, { headers: { Accept: "text/html" } })
    if (!response.ok) return
    const html = await response.text()
    this.appendPane(document.createRange().createContextualFragment(html))
    this.paneTargets.at(-1)?.focus()
  }

  appendPane(node) {
    while (this.paneTargets.length >= 3) this.removePane(this.paneTargets[0])
    this.paneStackTarget.appendChild(node)
  }

  removePane(pane) {
    if (pane.dataset.flow) this.flowController = null
    delete pane.dataset.workspaceTarget
    pane.addEventListener("transitionend", () => pane.remove(), { once: true })
    pane.classList.add("pane-closing")
    setTimeout(() => pane.isConnected && pane.remove(), 300)
  }

  closeTopPane() {
    const pane = this.paneTargets.at(-1)
    if (pane) this.removePane(pane)
    this.editorController()?.focus()
  }

  closePaneFromButton(event) {
    const pane = event.target.closest("[data-workspace-target='pane']")
    if (pane) this.removePane(pane)
    this.editorController()?.focus()
  }

  closeAllPanes() {
    this.paneTargets.forEach((pane) => this.removePane(pane))
  }

  // ---------- inspector ----------

  toggleInspector() {
    const existing = this.paneTargets.find((pane) => pane.dataset.inspector)
    if (existing) {
      this.removePane(existing)
      this.editorController()?.focus()
      return
    }
    this.appendPane(this.buildInspector())
    this.paneTargets.find((pane) => pane.dataset.inspector)?.focus()
  }

  buildInspector() {
    return buildInspectorPane({
      text: this.editorText(),
      onTravel: (from) => {
        const view = this.editorController()?.view
        if (!view) return
        view.dispatch({ selection: { anchor: from }, scrollIntoView: true })
        view.focus()
      },
      onOpenEntity: (entity) => this.openEntity({ detail: { url: entity.url, id: entity.id } }),
      onOpenFlow: (entity) => this.toggleFlow(entity.id),
      onHighlightChange: (entityId) => this.toggleEntityHighlight(entityId)
    })
  }

  rebuildInspector() {
    const existing = this.paneTargets.find((pane) => pane.dataset.inspector)
    if (!existing) return
    const fresh = this.buildInspector()
    existing.replaceWith(fresh)
  }

  toggleEntityHighlight(entityId) {
    store.toggleHighlight(entityId)
    const view = this.editorController()?.view
    if (view) repaintMentions(view)
    const pane = this.paneTargets.find((p) => p.dataset.inspector)
    if (pane) refreshInspectorHighlights(pane)
    this.saveOptions({ inspector: { highlighted: [...store.highlighted] } })
  }

  // ---------- history ----------

  async toggleHistory() {
    const existing = this.paneTargets.find((pane) => pane.dataset.history)
    if (existing) {
      this.removePane(existing)
      this.editorController()?.focus()
      return
    }
    const doc = store.documents.find((d) => d.id === this.documentIdValue)
    if (!doc) return
    const pane = await buildHistoryPane({
      snapshotsUrl: `${doc.url}/snapshots`,
      onRestored: (url) => Turbo.visit(url)
    })
    this.appendPane(pane)
    pane.querySelector("button")?.focus()
  }

  // ---------- entity kind & rename ----------

  topEntityPane() {
    return this.paneTargets.filter((pane) => pane.dataset.entityId).at(-1)
  }

  async changeEntityKind(entity, kind, badge = null) {
    const updated = await api(entity.url, { method: "PATCH", body: { entity: { kind } } })
    store.upsertEntity(updated)
    if (badge) {
      badge.className = `kind-badge ${KIND_TEXT_CLASSES[updated.kind]}`
      badge.replaceChildren()
      const sigil = document.createElement("span")
      sigil.className = "font-mono"
      sigil.textContent = updated.sigil
      badge.appendChild(sigil)
      badge.appendChild(document.createTextNode(updated.kind))
    }
    const view = this.editorController()?.view
    if (view) repaintMentions(view)
    this.showStatus(`Now a ${updated.kind}`)
  }

  cycleEntityKind(event) {
    const pane = event.target.closest("[data-workspace-target='pane']")
    const entity = store.findEntity(pane?.dataset.entityId)
    if (!entity) return
    const next = ALL_KINDS[(ALL_KINDS.indexOf(entity.kind) + 1) % ALL_KINDS.length]
    this.changeEntityKind(entity, next, event.target.closest(".kind-badge"))
  }

  // ---------- flow ----------

  async toggleFlow(focusEntityId = null) {
    const existing = this.paneTargets.find((pane) => pane.dataset.flow)
    if (existing && focusEntityId === null) {
      this.removePane(existing)
      this.editorController()?.focus()
      return
    }
    if (existing && this.flowController) {
      this.flowController.focusEntity(focusEntityId)
      existing.focus()
      return
    }

    this.flowController = new FlowPaneController({
      getText: () => this.editorText(),
      flowUrl: this.flowUrlValue,
      options: this.documentOptions.flow || {},
      onOptionsChanged: (flow) => this.saveOptions({ flow })
    })
    const pane = await this.flowController.build()
    this.appendPane(pane)
    if (focusEntityId !== null) this.flowController.focusEntity(focusEntityId)
    pane.focus()
  }

  saveOptions(patch) {
    this.documentOptions = { ...this.documentOptions, ...patch }
    clearTimeout(this.optionsTimer)
    this.optionsTimer = setTimeout(() => {
      const doc = store.documents.find((d) => d.id === this.documentIdValue)
      if (!doc) return
      api(doc.url, { method: "PATCH", body: { document: { options: this.documentOptions } } }).catch(() => {})
    }, 600)
  }

  // ---------- editor events ----------

  saving() {
    this.showStatus("Saving…", { sticky: true })
  }

  saved({ detail }) {
    if (detail.main) {
      this.showStatus("Saved · just now")
      this.lastSavedAt = new Date(detail.saved_at || Date.now())
      if (detail.version) this.refreshVersionReadout(detail.version)
      store.updateDocument({ id: detail.id, title: detail.title, updated_at: detail.saved_at })
      const item = this.binderTarget.querySelector(`[data-document-id="${detail.id}"]`)
      if (item && detail.title) item.textContent = detail.title
      this.rebuildInspector()
    } else {
      this.showStatus("Saved")
      if (detail.id) store.upsertEntity(detail)
    }
  }

  statusEvent({ detail }) {
    this.showStatus(detail.text, { sticky: detail.sticky })
  }

  overlayClicked(event) {
    if (event.target === this.paletteOverlayTarget) this.closePalette()
  }

  saveFailed() {
    this.showStatus("Couldn't save — retrying", { sticky: true })
  }

  wordcount({ detail: { words } }) {
    this.wordCountTarget.textContent = `${words.toLocaleString()} ${words === 1 ? "word" : "words"}`
  }

  showStatus(text, { sticky = false } = {}) {
    clearTimeout(this.statusTimer)
    this.saveStatusTarget.textContent = text
    this.saveStatusTarget.classList.remove("opacity-0")
    if (!sticky) {
      this.statusTimer = setTimeout(() => this.saveStatusTarget.classList.add("opacity-0"), 1600)
    }
  }

  // ---------- command palette ----------

  get paletteVisible() {
    return !this.paletteOverlayTarget.classList.contains("hidden")
  }

  openPalette() {
    this.paletteMode = null
    this.paletteOverlayTarget.classList.remove("hidden")
    this.paletteInputTarget.value = ""
    this.paletteInputTarget.placeholder = "Type a command, or the name of anything…"
    this.paletteInputTarget.focus()
    this.renderPalette()
  }

  closePalette() {
    this.paletteOverlayTarget.classList.add("hidden")
    this.editorController()?.focus()
  }

  paletteItems(query) {
    if (this.paletteMode?.type === "input") {
      return [{
        label: this.paletteMode.confirmLabel(query),
        detail: "enter",
        run: () => this.paletteMode.onSubmit(query)
      }]
    }
    if (this.paletteMode?.type === "confirm") {
      return [{ label: this.paletteMode.label, detail: "enter", run: this.paletteMode.onConfirm }]
    }

    const documents = store.documents
      .filter((doc) => doc.id !== this.documentIdValue)
      .map((doc) => ({
        label: doc.title,
        detail: doc.updated_at ? `document · ${timeAgo(new Date(doc.updated_at))}` : "document",
        run: () => Turbo.visit(doc.url)
      }))

    const entities = store.entities.map((entity) => ({
      label: entity.name,
      detail: entity.kind,
      run: () => {
        this.closePalette()
        this.openEntity({ detail: { url: entity.url, id: entity.id } })
      }
    }))

    const projects = store.projects
      .filter((project) => !project.current)
      .map((project) => ({ label: project.title, detail: "project", run: () => Turbo.visit(project.url) }))

    const commands = [
      { label: "New document", detail: "⌥⌘N", run: () => this.createDocument() },
      ...ENTITY_KINDS.map((kind) => ({
        label: `New ${kind}…`,
        detail: "create",
        run: () => this.enterInputMode({
          placeholder: `Name the ${kind}…`,
          confirmLabel: (text) => (text.trim() ? `Create ${kind} “${text.trim()}”` : `Name the ${kind} first`),
          onSubmit: async (text) => {
            const name = text.trim()
            if (!name) return
            const entity = await api(store.urls.entities, { method: "POST", body: { entity: { name, kind } } })
            store.upsertEntity(entity)
            this.closePalette()
            this.openEntity({ detail: { url: entity.url, id: entity.id } })
          }
        })
      })),
      { label: "Inspector", detail: "⌘⇧I", run: () => { this.closePalette(); this.toggleInspector() } },
      { label: "Flow", detail: "⌘⇧F", run: () => { this.closePalette(); this.toggleFlow() } },
      { label: "History", detail: "⌘⇧H", run: () => { this.closePalette(); this.toggleHistory() } },
      { label: "Toggle binder", detail: "esc", run: () => { this.closePalette(); this.toggleBinder() } },
      { label: "Close all panes", detail: "", run: () => { this.closeAllPanes(); this.closePalette() } },
      {
        label: "New project…",
        detail: "create",
        run: () => this.enterInputMode({
          placeholder: "Name the new project…",
          confirmLabel: (text) => (text.trim() ? `Create project “${text.trim()}”` : "Name the project first"),
          onSubmit: async (text) => {
            if (!text.trim()) return
            const response = await fetch("/projects", {
              method: "POST",
              headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
              body: JSON.stringify({ project: { title: text.trim() } })
            })
            if (response.ok) Turbo.visit(response.url)
          }
        })
      },
      {
        label: "Rename project…",
        detail: "create",
        run: () => this.enterInputMode({
          placeholder: "A new name for this project…",
          confirmLabel: (text) => (text.trim() ? `Rename project to “${text.trim()}”` : "Type the new name"),
          onSubmit: async (text) => {
            if (!text.trim()) return
            await api(this.projectUrlValue, { method: "PATCH", body: { project: { title: text.trim() } } })
            Turbo.visit(window.location.href)
          }
        })
      },
      {
        label: "Delete this document…",
        detail: "danger",
        run: () => {
          this.paletteMode = {
            type: "confirm",
            label: "Really delete this document? Its snapshots go with it.",
            onConfirm: () => this.deleteDocument()
          }
          this.paletteInputTarget.value = ""
          this.renderPalette()
        }
      }
    ]

    const paneEntity = store.findEntity(this.topEntityPane()?.dataset.entityId)
    const entityCommands = paneEntity
      ? [
          {
            label: `Rename ${paneEntity.name}…`,
            detail: "everywhere",
            run: () => this.enterInputMode({
              placeholder: `A new name for ${paneEntity.name}…`,
              confirmLabel: (text) => (text.trim()
                ? `Rename to “${text.trim()}” across the whole project`
                : "Type the new name"),
              onSubmit: async (text) => {
                if (!text.trim()) return
                await api(paneEntity.url, { method: "PATCH", body: { entity: { name: text.trim() } } })
                Turbo.visit(window.location.href)
              }
            })
          },
          ...ALL_KINDS.filter((kind) => kind !== paneEntity.kind).map((kind) => ({
            label: `Make ${paneEntity.name} a ${kind}`,
            detail: "kind",
            run: () => {
              this.closePalette()
              const badge = this.topEntityPane()?.querySelector(".kind-badge")
              this.changeEntityKind(paneEntity, kind, badge)
            }
          }))
        ]
      : []

    const everything = [...documents, ...entities, ...projects, ...entityCommands, ...commands]
    return query ? fuzzyFilter(query, everything, (item) => item.label) : everything
  }

  enterInputMode(mode) {
    this.paletteMode = { type: "input", ...mode }
    this.paletteInputTarget.value = ""
    this.paletteInputTarget.placeholder = mode.placeholder
    this.paletteInputTarget.focus()
    this.renderPalette()
  }

  async deleteDocument() {
    const doc = store.documents.find((d) => d.id === this.documentIdValue)
    if (!doc) return
    const response = await fetch(doc.url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken() }
    })
    if (response.ok) Turbo.visit(response.url)
  }

  paletteInputChanged() {
    this.paletteIndex = 0
    this.renderPalette()
  }

  paletteKeydown(event) {
    const count = Math.min((this.currentItems || []).length, 40)
    if (event.key === "ArrowDown" && count) {
      event.preventDefault()
      this.paletteIndex = (this.paletteIndex + 1) % count
      this.renderPalette()
    } else if (event.key === "ArrowUp" && count) {
      event.preventDefault()
      this.paletteIndex = (this.paletteIndex - 1 + count) % count
      this.renderPalette()
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.currentItems?.[this.paletteIndex]?.run()
    }
  }

  renderPalette() {
    this.currentItems = this.paletteItems(this.paletteInputTarget.value)
    this.paletteIndex = Math.min(this.paletteIndex, Math.max(0, this.currentItems.length - 1))
    this.paletteListTarget.replaceChildren()

    if (!this.currentItems.length) {
      const empty = document.createElement("li")
      empty.className = "px-4 py-6 text-center text-sm text-nord4/50 italic"
      empty.textContent = "Not all those who wander are lost — but this search is."
      this.paletteListTarget.appendChild(empty)
      return
    }

    this.currentItems.slice(0, 40).forEach((item, index) => {
      const li = document.createElement("li")
      li.className = `palette-item ${index === this.paletteIndex ? "is-highlighted" : ""}`

      const label = document.createElement("span")
      label.className = "flex-1 truncate"
      label.textContent = item.label
      li.appendChild(label)

      if (item.detail) {
        const detail = document.createElement("span")
        detail.className = "text-[10px] uppercase tracking-widest text-nord4/40"
        detail.textContent = item.detail
        li.appendChild(detail)
      }

      li.addEventListener("mousedown", (e) => e.preventDefault())
      li.addEventListener("click", () => item.run())
      this.paletteListTarget.appendChild(li)
    })

    this.paletteListTarget.children[this.paletteIndex]?.scrollIntoView({ block: "nearest" })
  }
}
